import { Component, inject, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ReportsResult } from '../../models/reports-reportsresult';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular';
import { DateTimeService } from '../../../shared/datetime/datetime.service';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';

type ReportRowValue = string | boolean | Date | null;
type ReportRow = Record<string, ReportRowValue>;
type ReportColumnKind = 'datetime' | 'bit' | 'other';

@Component({
    selector: 'app-reports-run-report-result',
    templateUrl: './reports-run-report-result.component.html',
    styleUrls: ['./reports-run-report-result.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, BizDataGridDirective, DxiColumnModule]
})
export class ReportsRunReportResultComponent {
    readonly dateTimeService = inject(DateTimeService);

    @ViewChild('dataGridVar', { static: false }) private dataGrid!: DxDataGridComponent;
    @Input() reportName = '';

    reportResult: ReportsResult | null = null;
    currentReportRows: ReportRow[] = [];

    refreshGrid(reportResult: ReportsResult): void {
        this.reportResult = reportResult;
        this.handleData();

        this.dataGrid?.instance.refresh();
    }

    convertDataType(type: string): 'number' | 'boolean' | 'date' | 'string' {
        switch (type.toLowerCase()) {
            case 'numeric':
                return 'number';
            case 'boolean':
                return 'boolean';
            case 'datetime':
                return 'date';
            default:
                return 'string';
        }
    }

    private handleData(): void {
        const rows = this.reportResult?.Rows ?? [];

        // Precompute the value-kind per column once to avoid repeated linear lookups per cell.
        const columnTypeByName = new Map<string, ReportColumnKind>(
            (this.reportResult?.Columns ?? []).map(column => {
                const valueType = column.ValueType.toLowerCase();
                return [column.Name, valueType === 'datetime' ? 'datetime' : (valueType === 'bit' ? 'bit' : 'other')] as const;
            })
        );

        this.currentReportRows = rows.map(row => {
            const tableRow: ReportRow = {};

            for (const value of row.Values) {
                const kind = columnTypeByName.get(value.ColumnName) ?? 'other';
                if (kind === 'datetime') {
                    tableRow[value.ColumnName] = new Date(value.Value);
                }
                else if (kind === 'bit') {
                    tableRow[value.ColumnName] = value.Value === '1';
                }
                else {
                    tableRow[value.ColumnName] = value.Value;
                }
            }

            return tableRow;
        });
    }
}
